import { useEffect, useMemo, useState } from 'react'

import { ActionIconEnum } from 'common/enums/ActionIcon'
import {
  BannerCapTorcedor,
  FeatureTopicList,
  ListDescription,
  Separator,
  TestimonialsCarousel,
} from 'components'
import BaseBridge from 'config/bridge/BaseBridge'
import { CheckoutBaseBridge } from 'config/bridge/CheckoutBaseBridge'
import { useLocalErrorHandler } from 'hooks/useLocalErrorHandler'
import { useNavBar } from 'hooks/useNavBar'
import { useHistory } from 'react-router-dom'
import { TypesRoutes } from 'routes/mixedRoutes/types'
import useBoundState from 'store'
import { hasAnyTicket } from 'store/afterSales/handleTickets'
import { Icons } from 'utils/icons'
import { Alert, BottomSheet, Button, formatToBRL, Tag, Text } from 'components/InterUI/InterUI'
import { shuffleArrayData } from 'utils/commons'
import heroBG from 'assets/png/hero.png'
import { TabIdEnum } from 'common/enums/TabIdEnum'
import { DeepLinksEnum } from 'common/enums/DeepLink'
import bannerCapTorcedor from 'assets/png/banner-cap-torcedor.png'

import { WaitingPayment } from './WaitingPayment/WaitingPayment'
import { Payment } from './Payment/Payment'
import { SummaryCheckout } from './SummaryCheckout/SummaryCheckout'
import { AnalyticsLandingPage, analyticsLandingPageLoad, analyticsRedeemNowTag } from './Home.tags'
import * as S from './Home.styles'

export const Home = () => {
  const history = useHistory()
  const handlerError = useLocalErrorHandler()
  const contentCapitalization = useBoundState((state) => state.contentCapitalization)
  const contentTitle = useBoundState((state) => state.contentTitle)
  const contentMyTitle = useBoundState((state) => state.contentMyTitle)

  const queryParams = useBoundState((state) => state.queryParams)
  const tickets = useBoundState((state) => state.tickets)
  const { enabled } = useBoundState((state) => state.eligibility)

  const emptyFormOfPayment = useBoundState((state) => state.emptyFormOfPayment)
  const getCheckoutResponse = useBoundState((state) => state.getCheckoutResponse)
  const setSelectedTab = useBoundState((state) => state.setSelectedTab)
  const testimonials = useBoundState((state) => state.testimonials)

  const SINGLE_TICKET_PRICE = 3
  const HAS_ANY_TICKET = hasAnyTicket(tickets)
  const HAS_REDEEMABLE_TICKETS = (tickets.totalRedemptionAmount ?? 0) > 0

  const [inWaitPayment, setInWaitPayment] = useState(false)
  const [eligibilityBS, setEligibilityBS] = useState(false)
  const [quantitySelected, setQuantitySelected] = useState(1)
  const [payout, setPayout] = useState(SINGLE_TICKET_PRICE)

  const canceledIcon = <Icons.Canceled width={32} height={32} color="var(--error500)" />

  const isDeepLinkRedemption = queryParams === DeepLinksEnum.REDEMPTION

  useNavBar({
    actionIcons: {
      rightIcon: ActionIconEnum.FAQ,
      leftIcon: ActionIconEnum.BACK,
    },
    backListener: () => BaseBridge.requestGoBack(),
  })

  useEffect(() => {
    AnalyticsLandingPage({ content_action: 'dado_carregado', adjust_event_token: '0' })
    analyticsLandingPageLoad({
      display_redemption_available: HAS_REDEEMABLE_TICKETS ? 'true' : 'false',
      display_redeem_now: HAS_REDEEMABLE_TICKETS ? 'true' : 'false',
      flow: isDeepLinkRedemption ? 'resgate' : 'prateleira',
    })

    BaseBridge.tacWb({
      name: 'load_home',
      module: 'capitalizacao',
      actions: ['enter_home'],
      params: {},
    })
  }, [])

  const ticketIcons = {
    gift: <Icons.Gift width={24} height={24} color="var(--primary500)" />,
    right: <Icons.ChevronRight width={24} height={24} color="var(--primary500)" />,
  }

  const handleCheckout = async () => {
    if (!enabled) {
      setEligibilityBS(true)
      return
    }

    AnalyticsLandingPage({
      ref_type: 'botão',
      content_action: 'toque',
      action_id: `Pagar - ${formatToBRL(payout)}`,
      adjust_event_token: '0',
    })

    try {
      const result = await CheckoutBaseBridge.contractWallet(`${payout}`)

      emptyFormOfPayment()
      setInWaitPayment(true)

      getCheckoutResponse({ history, capitalizationLot: result.transactionId })
    } catch (error) {
      const errorMessage = error as Error

      if (errorMessage.message !== 'walletCheckoutManagerDidUserCancelISafeAuthentication') {
        handlerError(errorMessage, 'LandingPage.handleCheckout')
      }
    }
  }

  const goToAfterSales = () => {
    AnalyticsLandingPage({
      product: 'capitalização',
      ref_type: 'botão',
      action_id: 'minhas sortezinhas',
      content_action: 'toque',
      redirect_to: HAS_REDEEMABLE_TICKETS ? 'resgate' : 'minhas sortezinhas',
      flow: isDeepLinkRedemption ? 'resgate' : 'prateleira',
    })

    HAS_REDEEMABLE_TICKETS
      ? setSelectedTab(TabIdEnum.REDEMPTION)
      : setSelectedTab(TabIdEnum.MY_TICKETS)
    history.push(TypesRoutes.AFTER_SALES)
  }

  const handleRedeemNow = () => {
    analyticsRedeemNowTag()
    setSelectedTab(TabIdEnum.REDEMPTION)
    history.push(TypesRoutes.AFTER_SALES)
  }

  const renderMyTickets = (
    <ListDescription
      margin="24px 0 0"
      iconLeft={ticketIcons.gift}
      iconRight={ticketIcons.right}
      withBorder
      onClick={goToAfterSales}
    >
      <S.MyTickets>
        <Text variant="body-3" colorWeight={500} bold data-testid="my-capitalizations-text">
          {contentMyTitle.value2}
        </Text>
        {HAS_REDEEMABLE_TICKETS && (
          <Text
            variant="caption-1"
            color="primary"
            style={{ textDecoration: 'underline' }}
            colorWeight={500}
            bold
          >
            Resgate disponível
          </Text>
        )}
      </S.MyTickets>
    </ListDescription>
  )

  const renderEligibilityBS = eligibilityBS && (
    <BottomSheet noSwipe onClose={() => setEligibilityBS(false)}>
      <S.BottomSheetContent>
        {canceledIcon}
        <Text variant="headline-h3" as="h3" semiBold>
          Não foi possível concluir sua compra
        </Text>
        <Text variant="body-3">
          A compra da {contentCapitalization.value1} não está disponível para a sua conta.
        </Text>
        <Button fullWidth variant="primary" onClick={() => setEligibilityBS(false)}>
          Entendi
        </Button>
      </S.BottomSheetContent>
    </BottomSheet>
  )

  const shuffledTestimonials = useMemo(() => shuffleArrayData(testimonials), [testimonials])

  return inWaitPayment ? (
    <WaitingPayment />
  ) : (
    <S.Container>
      <S.SectionMargin>{HAS_ANY_TICKET && renderMyTickets}</S.SectionMargin>

      <S.HeroContainer data-testid="hero-image">
        <S.HeroImage src={heroBG} alt="Hero" />
      </S.HeroContainer>

      <S.SectionMargin>
        <S.Header>
          <Tag theme="green">Por apenas R$ 3,00</Tag>
          <Text variant="headline-h1" as="h1" semiBold>
            A Capitalização do Inter com sorteios o ano todo!
          </Text>
        </S.Header>

        <FeatureTopicList />

        <S.Testimonials>
          <Text variant="headline-h3" as="h3" semiBold>
            Conheça os últimos ganhadores
          </Text>
          <TestimonialsCarousel testimonials={shuffledTestimonials} />
        </S.Testimonials>
        {HAS_REDEEMABLE_TICKETS && (
          <Alert type="grayscale" data-testid="redemption-alert">
            Você tem {contentTitle.value2Lowercase} disponíveis para resgate!
            <br />
            <Text
              variant="caption-1"
              color="primary"
              colorWeight={500}
              bold
              onClick={handleRedeemNow}
            >
              Resgatar agora
            </Text>
          </Alert>
        )}

        <BannerCapTorcedor img={bannerCapTorcedor} screen="home sortezinha" />
      </S.SectionMargin>

      {!HAS_REDEEMABLE_TICKETS && <Separator color="var(--gray100)" />}

      <S.SectionMargin>
        <Payment
          key="homePayment"
          quantitySelected={quantitySelected}
          payout={payout}
          setQuantitySelected={setQuantitySelected}
          setPayout={setPayout}
          singleTicketPrice={SINGLE_TICKET_PRICE}
        />
      </S.SectionMargin>

      <SummaryCheckout payout={payout} handleCheckout={handleCheckout} />

      {renderEligibilityBS}
    </S.Container>
  )
}
